<script>
  import { onDestroy, onMount } from 'svelte';
  import { transcribeAudio } from '../utils/api.js';

  // Props
  let {
    onTranscript = () => {},
    disabled = false,
    // Callback when recording state changes - receives { state, audioLevels, duration }
    onStateChange = () => {},
    // Compact mode: only show mic button, parent handles recording UI
    compact = false,
    // External trigger to stop recording (from parent's submit button)
    externalStop = false,
    // External trigger to cancel recording (from parent's cancel button)
    externalCancel = false
  } = $props();

  // State
  let state = $state('idle'); // 'idle' | 'starting' | 'recording' | 'processing'
  let error = $state(null);
  let recordingDuration = $state(0);
  const BAR_COUNT = 48;
  const emptyLevels = () => Array(BAR_COUNT).fill(0);
  let audioLevels = $state(emptyLevels());

  // Recording internals
  let mediaRecorder = null;
  let audioChunks = [];
  let audioContext = null;
  let analyser = null;
  let sourceNode = null; // Keep reference to prevent garbage collection
  let zeroGain = null; // Safari workaround: keeps audio graph "live"
  let animationFrame = null;
  let durationInterval = null;
  let stream = null;
  let isCancelled = false; // Flag to prevent processing on cancel
  let isVisualizationActive = false; // Non-reactive flag for animation loop
  let recordingEpoch = 0;

  // Ensure AudioContext and Analyser exist (Safari fix: create once, keep alive)
  function ensureAudioGraph() {
    if (!audioContext || audioContext.state === 'closed') {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      audioContext = new AudioContextClass();
    }

    if (!analyser) {
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;

      // Safari workaround: connect analyser to a muted output so the graph is definitely "live"
      zeroGain = audioContext.createGain();
      zeroGain.gain.value = 0;
      analyser.connect(zeroGain);
      zeroGain.connect(audioContext.destination);
    }
  }

  // Handle Escape key to cancel recording
  function handleKeydown(event) {
    if (event.key === 'Escape' && (state === 'starting' || state === 'recording')) {
      event.preventDefault();
      cancelRecording();
    }
  }

  onMount(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  });

  // Notify parent when state, audio levels, or duration changes
  $effect(() => {
    onStateChange({
      state,
      audioLevels,
      duration: recordingDuration
    });
  });

  // React to external stop signal
  $effect(() => {
    if (externalStop && state === 'recording') {
      stopRecording();
    }
  });

  // React to external cancel signal
  $effect(() => {
    if (externalCancel && (state === 'starting' || state === 'recording')) {
      cancelRecording();
    }
  });

  async function startRecording() {
    if (state !== 'idle' || disabled) return;
    state = 'starting';
    const epoch = ++recordingEpoch;
    error = null;
    audioChunks = [];
    recordingDuration = 0;
    isCancelled = false;
    isVisualizationActive = false;

    try {
      // IMPORTANT: Call ensureAudioGraph synchronously in the click handler (Safari/iOS requirement)
      ensureAudioGraph();

      // Build audio constraints - optimized for speech transcription
      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,      // Mono - sufficient for speech, reduces file size
        sampleRate: 16000,    // 16kHz - optimal for speech recognition APIs
        sampleSize: 16        // 16-bit audio
      };

      console.log('🎤 Requesting audio with constraints:', audioConstraints);

      // Kick off BOTH operations before any await (critical for iOS Safari user gesture)
      const resumePromise = audioContext.state === 'suspended'
        ? audioContext.resume()
        : Promise.resolve();

      const gumPromise = navigator.mediaDevices.getUserMedia({ audio: audioConstraints }).then(micStream => {
        // Permission can resolve after cancellation, destruction, or a resume failure.
        if (epoch !== recordingEpoch) {
          micStream.getTracks().forEach(track => track.stop());
          return null;
        }
        stream = micStream;
        return micStream;
      });

      // Now await both in parallel
      await Promise.all([resumePromise, gumPromise]);
      if (epoch !== recordingEpoch) return;

      // Hook mic stream to analyser
      sourceNode = audioContext.createMediaStreamSource(stream);
      sourceNode.connect(analyser);

      console.log('🎤 Audio graph connected, context state:', audioContext.state);

      // Set up MediaRecorder with optimized settings for speech
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 64000  // 64kbps - good quality for speech, small file size
      });

      mediaRecorder.ondataavailable = (event) => {
        if (epoch !== recordingEpoch) return;
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (epoch !== recordingEpoch) return;
        // Stop visualization
        isVisualizationActive = false;
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
          animationFrame = null;
        }

        // Only process if not cancelled and has data
        if (!isCancelled && audioChunks.length > 0) {
          await processRecording(epoch);
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms

      // CRITICAL: Set state and start visualization AFTER everything is set up
      state = 'recording';
      isVisualizationActive = true;
      visualize();

      // Start duration counter
      durationInterval = setInterval(() => {
        recordingDuration += 1;
      }, 1000);

    } catch (err) {
      if (epoch !== recordingEpoch) return;
      recordingEpoch += 1;
      console.error('Failed to start recording:', err);
      isVisualizationActive = false;
      if (err.name === 'NotAllowedError') {
        error = 'Microphone access denied. Please allow microphone access to use voice input.';
      } else if (err.name === 'NotFoundError') {
        error = 'No microphone found. Please connect a microphone.';
      } else {
        error = 'Failed to start recording. Please try again.';
      }
      cleanup();
      state = 'idle';
    }
  }

  function visualize() {
    if (!isVisualizationActive || !analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let smoothedLevels = Array(BAR_COUNT).fill(0);
    let debugCounter = 0;

    console.log('🎤 Visualizer started using AnalyserNode (Safari-compatible)');

    function draw() {
      if (!isVisualizationActive) return;

      animationFrame = requestAnimationFrame(draw);

      // Use byte API for max Safari compatibility
      analyser.getByteTimeDomainData(dataArray);

      // Calculate volume from time domain data
      // dataArray values are 0-255 where 128 is the center line (silence)
      let sumSquares = 0;
      let peak = 0;
      for (let i = 0; i < bufferLength; i++) {
        // Convert to -1 to 1 range
        const sample = (dataArray[i] - 128) / 128;
        sumSquares += sample * sample;
        peak = Math.max(peak, Math.abs(sample));
      }

      const rms = Math.sqrt(sumSquares / bufferLength);
      // Combine RMS and peak, amplify for visibility
      const volume = Math.min(1, (rms * 8) + (peak * 2));

      debugCounter++;
      if (debugCounter % 60 === 0) {
        console.log('🎤 Volume level:', {
          volume: volume.toFixed(3),
          contextState: audioContext?.state
        });
      }

      const barCount = BAR_COUNT;
      const newLevels = [];

      for (let i = 0; i < barCount; i++) {
        const centerWeight = 1 - Math.abs(i - 3) / 4;
        const barVolume = volume * (0.7 + centerWeight * 0.3);

        if (barVolume > smoothedLevels[i]) {
          smoothedLevels[i] = barVolume * 0.75 + smoothedLevels[i] * 0.25;
        } else {
          smoothedLevels[i] = smoothedLevels[i] * 0.85;
        }

        newLevels.push(Math.min(1, Math.max(0.05, smoothedLevels[i])));
      }

      audioLevels = [...newLevels];
    }

    draw();
  }

  function stopRecording() {
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }

    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
    // Note: isVisualizationActive is set to false in mediaRecorder.onstop
  }

  function cancelRecording() {
    recordingEpoch += 1;
    isCancelled = true; // Prevent onstop from processing
    isVisualizationActive = false; // Stop visualization immediately
    audioChunks = [];

    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }

    stopRecording();
    cleanup();
    state = 'idle';
    audioLevels = emptyLevels();
  }

  async function processRecording(epoch) {
    state = 'processing';

    try {
      const mimeType = mediaRecorder?.mimeType || 'audio/webm';
      const audioBlob = new Blob(audioChunks, { type: mimeType });
      cleanup();

      // Minimum recording length check (at least 0.5 seconds)
      if (audioBlob.size < 1000) {
        error = 'Recording too short. Please try again.';
        state = 'idle';
        cleanup();
        return;
      }

      const transcript = await transcribeAudio(audioBlob);
      if (epoch !== recordingEpoch) return;

      if (transcript && transcript.trim()) {
        onTranscript(transcript.trim());
      } else {
        error = 'No speech detected. Please try again.';
      }
    } catch (err) {
      if (epoch !== recordingEpoch) return;
      console.error('Transcription error:', err);
      error = err.message || 'Failed to transcribe audio. Please try again.';
    } finally {
      if (epoch === recordingEpoch) {
        cleanup();
        state = 'idle';
        audioLevels = emptyLevels();
      }
    }
  }

  function cleanup() {
    isVisualizationActive = false;
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }

    // Disconnect source from analyser but keep audioContext/analyser alive for reuse
    if (sourceNode) {
      sourceNode.disconnect();
      sourceNode = null;
    }
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      stream = null;
    }
    // Note: We intentionally do NOT close audioContext or destroy analyser
    // Keeping them alive is the Safari fix - they can be reused for next recording
    mediaRecorder = null;
  }

  // Full cleanup for component destruction
  function fullCleanup() {
    cleanup();
    if (zeroGain) {
      zeroGain.disconnect();
      zeroGain = null;
    }
    if (analyser) {
      analyser.disconnect();
      analyser = null;
    }
    if (audioContext) {
      audioContext.close();
      audioContext = null;
    }
  }

  function formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function handleClick() {
    if (state === 'idle') {
      startRecording();
    }
  }

  onDestroy(() => {
    recordingEpoch += 1;
    isCancelled = true;
    isVisualizationActive = false;
    if (durationInterval) clearInterval(durationInterval);
    if (animationFrame) cancelAnimationFrame(animationFrame);
    fullCleanup();
  });
</script>

<div class="voice-recorder" class:recording={state === 'recording'} class:processing={state === 'processing'} class:compact>
  {#if compact}
    <!-- Compact mode: only show mic button, parent handles recording UI -->
    {#if state === 'idle'}
      <button
        class="mic-button compact-mic"
        onclick={handleClick}
        disabled={disabled}
        title="Click to record your vision"
        aria-label="Start voice recording"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      </button>
    {:else if state === 'starting' || state === 'processing'}
      <div class="compact-processing" role="status" aria-label={state === 'starting' ? 'Starting microphone' : 'Transcribing'}>
        <div class="processing-spinner"></div>
      </div>
    {/if}
    <!-- In compact recording mode, parent renders the UI using state callback -->
  {:else}
    <!-- Full mode: component handles all UI -->
    {#if state === 'idle'}
      <!-- Idle: Mic button -->
      <button
        class="mic-button"
        onclick={handleClick}
        disabled={disabled}
        title="Click to record your vision"
        aria-label="Start voice recording"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      </button>

    {:else if state === 'starting'}
      <div class="processing-container" role="status">
        <div class="processing-spinner"></div>
        <span class="processing-text">Starting microphone…</span>
        <button class="cancel-button" onclick={cancelRecording} aria-label="Cancel microphone request">Cancel</button>
      </div>
    {:else if state === 'recording'}
      <!-- Recording: Waveform + controls -->
      <div class="recording-container">
        <button
          class="cancel-button"
          onclick={cancelRecording}
          title="Cancel recording (Esc)"
          aria-label="Cancel recording"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div class="waveform-container">
          <div class="waveform">
            {#each audioLevels as level, i}
              <div
                class="waveform-bar"
                style="height: {4 + level * 36}px;"
              ></div>
            {/each}
          </div>
          <span class="duration">{formatDuration(recordingDuration)}</span>
        </div>

        <button
          class="send-button"
          onclick={stopRecording}
          title="Send recording"
          aria-label="Stop and transcribe"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      </div>

    {:else if state === 'processing'}
      <!-- Processing: Spinner -->
      <div class="processing-container">
        <div class="processing-spinner"></div>
        <span class="processing-text">Transcribing...</span>
      </div>
    {/if}
  {/if}

  {#if error}
    <div class="voice-error">
      {error}
      <button class="dismiss-error" onclick={() => error = null}>Dismiss</button>
    </div>
  {/if}
</div>

<style>
  .voice-recorder {
    display: inline-flex;
    align-items: center;
    position: relative;
  }

  /* Compact mode styles */
  .voice-recorder.compact .compact-mic {
    width: 36px;
    height: 36px;
    min-height: 36px;
    max-height: 36px;
    border-radius: 50%;
    border: 1px solid var(--color-border);
    background: transparent;
  }

  .voice-recorder.compact .compact-mic svg {
    width: 18px;
    height: 18px;
  }

  .compact-processing {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Mic container for button + dropdown trigger */
  /* Mic button - idle state */
  .mic-button {
    width: 48px;
    height: 48px;
    min-height: 48px;
    max-height: 48px;
    border-radius: 8px;
    background: var(--color-surface);
    border: 2px solid var(--color-border);
    color: var(--color-text-muted);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .mic-button:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: var(--color-primary-light);
    transform: scale(1.05);
  }

  .mic-button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .mic-button svg {
    width: 22px;
    height: 22px;
  }

  /* Recording container */
  .recording-container {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--color-primary-light);
    border: 2px solid var(--color-primary);
    border-radius: 30px;
    padding: 6px 12px;
    animation: expandIn 0.3s ease;
  }

  @keyframes expandIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Cancel button */
  .cancel-button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .cancel-button:hover {
    color: var(--color-error);
    background: rgba(239, 68, 68, 0.1);
  }

  .cancel-button svg {
    width: 18px;
    height: 18px;
  }

  /* Waveform visualization */
  .waveform-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 100px;
  }

  .waveform {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 4px;
    height: 40px;
  }

  .waveform-bar {
    width: 5px;
    background: linear-gradient(to top, var(--color-primary), var(--color-primary-hover, #ff4da6));
    border-radius: 3px;
    transition: height 0.06s ease-out;
    box-shadow: 0 0 6px rgba(255, 27, 141, 0.4);
  }

  .duration {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-primary);
    font-variant-numeric: tabular-nums;
  }

  /* Send button */
  .send-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--color-primary);
    border: none;
    color: white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(255, 27, 141, 0.4);
  }

  .send-button:hover {
    background: var(--color-primary-hover);
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(255, 27, 141, 0.5);
  }

  .send-button svg {
    width: 20px;
    height: 20px;
  }

  /* Processing state */
  .processing-container {
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--color-surface);
    border: 2px solid var(--color-border);
    border-radius: 30px;
    padding: 8px 16px;
  }

  .processing-spinner {
    width: 20px;
    height: 20px;
    border: 3px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .processing-text {
    font-size: 14px;
    color: var(--color-text-muted);
    font-weight: 500;
  }

  /* Error display */
  .voice-error {
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: var(--color-error);
    color: white;
    padding: 10px 14px;
    border-radius: var(--radius);
    font-size: 13px;
    white-space: nowrap;
    max-width: 280px;
    white-space: normal;
    text-align: center;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    animation: slideDown 0.2s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  .dismiss-error {
    display: block;
    margin-top: 8px;
    margin-left: auto;
    margin-right: auto;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    min-height: auto;
  }

  .dismiss-error:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* Responsive adjustments */
  @media (max-width: 480px) {
    .recording-container {
      gap: 8px;
      padding: 4px 8px;
    }

    .waveform-container {
      min-width: 80px;
    }

    .waveform {
      gap: 2px;
    }

    .waveform-bar {
      width: 3px;
    }
  }
</style>
